import React from "react";
import DataTable from "./DataTable";
import MatrixView from "./MatrixView";
import ChartRenderer from "./ChartRenderer";
import analysisTypes from "./analysisTypes";

const HEATMAP_TYPES = new Set(["correlation", "pca", "cluster"]);
const MATRIX_TITLES = {
  chisquare: "Observed vs. expected",
  logistic: "Confusion matrix",
  anova: "Tukey pairwise comparisons",
  turf: "Greedy selection steps",
  correlation: "Correlation heatmap",
  pca: "Component loadings",
  cluster: "Cluster profile (variable means)",
};

function formatMetric(value, tile) {
  if (value === null || value === undefined) return "—";
  if (tile.percent) return `${(value * 100).toFixed(1)}%`;
  const decimals = tile.decimals ?? 3;
  return typeof value === "number" ? value.toFixed(decimals) : String(value);
}

/** Renders one saved/just-run analysis envelope: metric tiles, narrative, ANOVA
 * table, coefficients table (with CI/VIF columns toggled by displayOptions),
 * chart, residual/ROC diagnostic plots, matrix view. */
function AnalysisResults({ analysisType, analysisLabel, result, displayOptions }) {
  if (!result) return null;
  const { summary, table, matrix, chart, narrative, anovaTable, residualChart, rocChart } = result;
  const typeConfig = analysisTypes.find((a) => a.id === analysisType || a.engineId === analysisType);
  const metricTiles = typeConfig?.metricTiles;

  const excludeColumns = [];
  if (displayOptions && displayOptions.showCI === false) {
    excludeColumns.push("ci_low", "ci_high", "or_ci_low", "or_ci_high");
  }
  if (displayOptions && displayOptions.showVif === false) {
    excludeColumns.push("vif");
  }

  return (
    <div className="analysis-results-block">
      <h3>{analysisLabel}</h3>
      {narrative && <p className="analysis-narrative">{narrative}</p>}

      {metricTiles ? (
        <div className="analysis-summary-badges">
          {metricTiles.map((tile) => (
            <div className="analysis-badge" key={tile.key}>
              <span className="analysis-badge-label">{tile.label}</span>
              <span className="analysis-badge-value">{formatMetric(summary?.[tile.key], tile)}</span>
            </div>
          ))}
        </div>
      ) : (
        summary && Object.keys(summary).length > 0 && (
          <div className="analysis-summary-badges">
            {Object.entries(summary).map(([k, v]) => (
              Array.isArray(v) || (v && typeof v === "object") ? null : (
                <div className="analysis-badge" key={k}>
                  <span className="analysis-badge-label">{k}</span>
                  <span className="analysis-badge-value">{typeof v === "number" ? (Number.isInteger(v) ? v : v.toFixed(3)) : String(v)}</span>
                </div>
              )
            ))}
          </div>
        )
      )}

      {anovaTable && <DataTable rows={anovaTable} title="ANOVA" exportName={`${analysisType}_anova`} />}

      <ChartRenderer chart={chart} />

      <DataTable rows={table} title="Coefficients / Results" exportName={`${analysisType}_results`} excludeColumns={excludeColumns} />

      {residualChart && (!displayOptions || displayOptions.showResidualPlot !== false) && (
        <ChartRenderer chart={residualChart} />
      )}
      {rocChart && (!displayOptions || displayOptions.showRoc !== false) && (
        <ChartRenderer chart={rocChart} />
      )}

      <MatrixView matrix={matrix} heatmap={HEATMAP_TYPES.has(analysisType)} title={MATRIX_TITLES[analysisType]} />
    </div>
  );
}

export default AnalysisResults;
